import i18n from 'internationalization/i18n';
import { encryptData } from './encryptData';
import { setTabScope } from './environmentScope';

interface EnvironmentData {
    environment_hash: string;
    type: 'trial' | 'beta' | 'api-partner' | 'client' | 'demo' | 'admin';
    status: 'active' | 'expired' | 'suspended' | 'cancelled';
    package_features: string[];
    active_features: string[];
    view_only_package?: boolean;
    redirect_to: 'trial-wizard/1' | 'trial-homepage' | 'dashboard' | 'admin-dashboard' | 'settings/admin/environments' | 'setup-2fa';
}

interface UserData {
    user_hash: string;
    user_division: number | null;
    first_name: string;
    last_name: string;
    access_to: string[];
    role_features: string[];
    allowed_features: string[];
    allowed_rights: string[];
}

interface SubscriptionData {
    is_trial: boolean | null;
    start_date: string | null;
    end_date: string | null;
    no_payment_method: boolean;
    is_provisional: boolean;
}

export interface LoginResponse {
    environment_data: EnvironmentData;
    user_data: UserData;
    subscription_data: SubscriptionData;
    language: string | null;
}

export interface LoginCallbacks {
    setIsLoggedIn: (loggedIn: boolean | null) => void;
    setUserHash: (userHash: string | undefined) => void;
    setUserDivision: (userDivision: number | null) => void;
    setFirstName: (firstName: string | null) => void;
    setLastName: (lastName: string | null) => void;
    setEnvironmentHash: (value: string) => void;
    setEnvironmentType: (value: 'trial' | 'beta' | 'api-partner' | 'client' | 'demo' | 'admin') => void;
    setEnvironmentStatus: (value: 'active' | 'expired' | 'suspended' | 'cancelled') => void;
    setPackageFeatures: (value: string[]) => void;
    setActiveFeatures: (value: string[]) => void;
    setRoleFeatures: (value: string[]) => void;
    setAccessTo: (value: string[]) => void;
    setAllowedFeatures: (value: string[]) => void;
    setAllowedRights: (value: string[]) => void;
    setRedirectTo: (value: 'trial-wizard/1' | 'trial-homepage' | 'dashboard' | 'admin-dashboard' | 'settings/admin/environments' | 'setup-2fa' | null) => void;
    setSubscriptionIsTrial: (value: boolean | null) => void;
    setSubscriptionStartDate: (value: string | null) => void;
    setSubscriptionEndDate: (value: string | null) => void;
    setSubscriptionNoSepaMandate: (value: boolean) => void;
    setSubscriptionIsProvisional: (value: boolean) => void;
    setPackageIsViewOnly: (value: boolean) => void;
    setStatesLoaded: (isLoaded: boolean) => void;
}

interface SessionDataParams {
    responseData: LoginResponse;
    callbacks: LoginCallbacks;
}

// Sets the states of the authentication context from the session payload
const applySessionDataToState = ({ responseData, callbacks }: SessionDataParams) => {
    // Unpack the response data
    const { environment_data, user_data, subscription_data } = responseData;

    // Set the language if it's given
    if (responseData.language) {
        i18n.changeLanguage(responseData.language);
    }

    // load context state
    callbacks.setIsLoggedIn(true);
    callbacks.setUserHash(user_data.user_hash);
    callbacks.setUserDivision(user_data.user_division);
    callbacks.setFirstName(user_data.first_name);
    callbacks.setLastName(user_data.last_name);
    callbacks.setAccessTo(user_data.access_to);
    callbacks.setRoleFeatures(user_data.role_features);
    callbacks.setAllowedFeatures(user_data.allowed_features);
    callbacks.setAllowedRights(user_data.allowed_rights);
    callbacks.setEnvironmentHash(environment_data.environment_hash);
    callbacks.setEnvironmentType(environment_data.type);
    callbacks.setEnvironmentStatus(environment_data.status);
    callbacks.setPackageFeatures(environment_data.package_features);
    callbacks.setActiveFeatures(environment_data.active_features);
    callbacks.setPackageIsViewOnly(environment_data.view_only_package === true);
    callbacks.setRedirectTo(environment_data.redirect_to);
    callbacks.setSubscriptionIsTrial(subscription_data.is_trial);
    callbacks.setSubscriptionStartDate(subscription_data.start_date);
    callbacks.setSubscriptionEndDate(subscription_data.end_date);
    callbacks.setSubscriptionNoSepaMandate(subscription_data.no_payment_method);
    callbacks.setSubscriptionIsProvisional(subscription_data.is_provisional);
    callbacks.setStatesLoaded(true);
};

// Stores the session payload encrypted in the local storage as default session of new tabs
const persistSessionDataToLocalStorage = ({ environment_data, user_data, subscription_data }: LoginResponse) => {
    // Encrypt and save user data
    for (const [key, value] of Object.entries(user_data)) {
        const encryptedValue = encryptData(JSON.stringify(value));
        localStorage.setItem(key, encryptedValue);
    }

    // Encrypt and save environment data
    for (const [key, value] of Object.entries(environment_data)) {
        const encryptedValue = encryptData(JSON.stringify(value));
        localStorage.setItem(key, encryptedValue);
    }

    // Encrypt and save subscription data
    for (const [key, value] of Object.entries(subscription_data)) {
        const encryptedValue = encryptData(JSON.stringify(value));
        localStorage.setItem(key, encryptedValue);
    }

    // Set flag to start session checks in the local storage
    localStorage.setItem('currentSession', 'true');
};

// Loads a login, verification or switch response: it becomes the default session
// of every new tab and pins this tab to the environment of the response
export const loadSessionData = ({ responseData, callbacks }: SessionDataParams) => {
    applySessionDataToState({ responseData, callbacks });
    persistSessionDataToLocalStorage(responseData);
    setTabScope(responseData.environment_data.environment_hash, responseData.user_data.user_hash);
};

// Loads the session payload of a tab pinned to another environment than the default
// session, the local storage is left untouched so new tabs keep the default environment
export const loadTabSessionData = ({ responseData, callbacks }: SessionDataParams) => {
    applySessionDataToState({ responseData, callbacks });
    setTabScope(responseData.environment_data.environment_hash, responseData.user_data.user_hash);
};